/* Range results, from the Deck's side.
 *
 * The Deck reads batches; it cannot start one. A button that kicked a two-hundred-run batch would
 * tie up the same Gateway process that is serving the live exercise, and the first symptom would
 * be the clock falling behind for twenty minutes with no explanation. Batches are a command-line
 * act; this is the window onto what they produced.
 *
 * Every judgement about a row — whether it carries information, whether the interval is wide —
 * comes from the server. Two implementations of "is this measure readable?" would eventually
 * disagree, and the one on screen would be the one nobody tested.
 */

import { authHeaders, baseUrl } from './gateway'

/** How confidently a row can be read. The server decides; see `experiments._verdict`. */
export type Grade = 'ok' | 'weak' | 'none'

export interface MeasureRow {
  name: string
  /** `proportion` (Wilson) or `mean` (Student-t) — a rate and a count are not the same claim.
   *  `unknown` for batches written before Range recorded the kinds; never inferred. */
  kind: string
  description: string
  estimate: number | null
  low: number | null
  high: number | null
  n: number
  method: string
  /** Runs where the measure applied, and where it did not. A measure that never applied has no
   *  estimate of zero — it has no estimate. */
  observed: number
  missing: number
  distinct: number
  min: number | null
  max: number | null
  stdev: number
  grade: Grade
  /** Why the grade is what it is, in words. Shown verbatim. */
  note: string
}

export interface BatchSummary {
  id: string
  experiment: string
  createdIso: string
  runs: number
  failed: number
  ticks: number
  wallSeconds: number
  measures: number
  /** `verified`, `FAILED`, or `unverified`. A FAILED batch is not a slow result, it is an
   *  invalid one, and every interval in it is measuring noise. */
  determinism: string
  datasetDigest: string
  scenarioDigest: string
  scenario: string
  engineVersion: string
}

export interface BatchRun {
  seed: number
  ticks: number
  wallSeconds: number
  digest: string
  values: Record<string, number | boolean | null>
  error: string
}

export interface BatchDocument extends BatchSummary {
  manifest: Record<string, unknown>
  determinismDetail: string
  rows: MeasureRow[]
  runs_: BatchRun[]
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${baseUrl()}${path}`, { headers: authHeaders() })
  if (res.ok) return (await res.json()) as T
  // The Gateway's `detail` says which role was refused and why. Replacing it with a status code
  // sends the reader to the logs for something the server already said in a sentence.
  let detail = `HTTP ${res.status}`
  try {
    const body = (await res.json()) as { detail?: string }
    if (body.detail) detail = body.detail
  } catch {
    /* non-JSON error body */
  }
  throw new Error(detail)
}

export async function fetchBatches(): Promise<BatchSummary[]> {
  const body = await get<{ experiments: BatchSummary[] }>('/v1/experiments')
  return body.experiments
}

export async function fetchBatch(id: string): Promise<BatchDocument> {
  const body = await get<Omit<BatchDocument, 'runs_'> & { runs: unknown }>(
    `/v1/experiments/${encodeURIComponent(id)}`,
  )
  // `runs` is a COUNT in the summary and a LIST in the document — the API says both because a
  // list view needs the count and a reader needs the records. Split here rather than letting a
  // component discover which one it got.
  const records = Array.isArray(body.runs) ? (body.runs as BatchRun[]) : []
  return { ...(body as unknown as BatchDocument), runs: records.length, runs_: records }
}

/** A measure's value formatted for its kind: a rate as a percentage, a count as a number. */
export function formatValue(kind: string, v: number | null): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  if (kind === 'proportion') return `${(v * 100).toFixed(1)}%`
  if (Math.abs(v) >= 100) return v.toFixed(0)
  return v.toFixed(2)
}

/** The interval as a reader should see it, or an explicit refusal when there is nothing to show. */
export function formatInterval(row: MeasureRow): string {
  if (row.grade === 'none' || row.low === null || row.high === null) return '—'
  return `${formatValue(row.kind, row.low)} … ${formatValue(row.kind, row.high)}`
}

/** What the method column says, spelled out rather than abbreviated.
 *
 *  A reader who does not know what "wilson" means cannot tell that the row above and the row
 *  below are different kinds of claim — which is the one thing this column exists to convey. */
export function methodLabel(row: MeasureRow): string {
  if (row.method === 'wilson') return 'Wilson (rate)'
  if (row.method === 't') return 'Student-t (mean)'
  return row.method || '—'
}
